import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import SEOHead from '../../components/SEOHead';
import img1 from '../../assets/marketplaces2.png';
import img2 from '../../assets/marketplaces.png';
import './ServicePage.css';

const SEO = {
  title: 'Noon Marketplace Management Services | Mad Marketing Club',
  description: 'Noon.com marketplace management for UAE, Saudi Arabia & Egypt — seller setup, product listing optimization, Noon Ads & Middle East e-commerce growth. Free consultation.',
  canonical: 'https://madmarketingclub.com/services/noon',
  breadcrumb: [
    { name: 'Home', url: 'https://madmarketingclub.com/' },
    { name: 'Services', url: 'https://madmarketingclub.com/#services' },
    { name: 'Noon', url: 'https://madmarketingclub.com/services/noon' },
  ],
  service: { name: 'Noon Marketplace Management Services', type: 'Noon Marketplace Management' },
};

const OVERVIEW_POINTS = [
  'Access millions of regional shoppers',
  'Expand across UAE, KSA, and Egypt',
  'Utilize Noon Ads for product visibility',
  'Leverage Fulfilled by Noon (FBN)',
  'Participate in major sales events',
  'Access Seller Lab analytics and reporting',
  'Build brand presence across GCC markets',
];

const APPROACH = [
  'Marketplace launch & onboarding',
  'Seller Lab optimization',
  'Product catalog management',
  'Noon SEO optimization',
  'Noon Ads management',
  'FBN & logistics support',
  'Inventory forecasting',
  'Conversion optimization',
  'Account health monitoring',
  'Revenue growth strategies',
];

const PROCESS = [
  {
    num: '01',
    title: 'Business Consultation & Discovery',
    intro: 'We begin by understanding:',
    points: [
      'Your products',
      'Target countries',
      'Competitor landscape',
      'Pricing strategy',
      'Current marketplace presence',
      'Growth objectives',
    ],
  },
  {
    num: '02',
    title: 'Noon Account Audit',
    intro: 'Our team reviews:',
    points: [
      'Product listings',
      'Content quality',
      'Search visibility',
      'Inventory performance',
      'Advertising structure',
      'Seller performance metrics',
      'Account compliance',
    ],
  },
  {
    num: '03',
    title: 'Marketplace Growth Strategy',
    intro: 'We develop a customized roadmap covering:',
    points: [
      'Listing optimization',
      'Advertising strategy',
      'Inventory planning',
      'Promotional campaigns',
      'Fulfillment recommendations',
      'Market expansion opportunities',
    ],
  },
  {
    num: '04',
    title: 'Execution & Optimization',
    intro: 'Our team implements:',
    points: [
      'Catalog creation',
      'SEO improvements',
      'Ad campaign setup',
      'Store optimization',
      'Pricing updates',
      'Performance enhancements',
    ],
  },
  {
    num: '05',
    title: 'Reporting & Scaling',
    intro: 'Continuous monitoring includes:',
    points: [
      'Sales performance',
      'Advertising ROI',
      'Product rankings',
      'Inventory status',
      'Marketplace opportunities',
      'Growth recommendations',
    ],
  },
];

const SERVICES = [
  {
    title: 'Noon Seller Account Setup & Onboarding',
    gradient: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
      </svg>
    ),
    points: [
      'Seller registration assistance',
      'Account verification support',
      'Store setup',
      'Documentation guidance',
      'Legal entity submission support',
      'Country-specific onboarding',
      'Seller Lab configuration',
    ],
  },
  {
    title: 'Product Listing & Catalog Management',
    gradient: 'linear-gradient(135deg, #2563eb, #0891b2)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14,2 14,8 20,8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
        <polyline points="10,9 9,9 8,9" />
      </svg>
    ),
    points: [
      'Product listing creation',
      'Catalog uploads',
      'Variation setup',
      'Product data optimization',
      'Listing issue resolution',
      'Catalog maintenance',
      'Product attribute optimization',
    ],
  },
  {
    title: 'Noon SEO Services',
    gradient: 'linear-gradient(135deg, #7c3aed, #a855f7)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
      </svg>
    ),
    points: [
      'Keyword research',
      'Competitor analysis',
      'Product title optimization',
      'Feature optimization',
      'Search term enhancement',
      'Conversion-focused content',
      'Ranking improvement strategies',
    ],
  },
  {
    title: 'Noon Ads Management',
    gradient: 'linear-gradient(135deg, #ea580c, #f59e0b)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="20" x2="18" y2="10" />
        <line x1="12" y1="20" x2="12" y2="4" />
        <line x1="6" y1="20" x2="6" y2="14" />
        <line x1="2" y1="20" x2="22" y2="20" />
      </svg>
    ),
    points: [
      'Sponsored product campaigns',
      'Keyword targeting',
      'Campaign optimization',
      'Bid management',
      'Budget allocation',
      'ACOS optimization',
      'Sales growth campaigns',
    ],
  },
  {
    title: 'Fulfillment & Logistics Support',
    gradient: 'linear-gradient(135deg, #db2777, #e879f9)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <polyline points="21 15 16 10 5 21" />
      </svg>
    ),
    points: [
      'Fulfilled by Noon (FBN) support',
      'Fulfillment strategy consulting',
      'Inventory planning',
      'Warehouse coordination',
      'Shipment management',
      'Return process guidance',
      'Logistics optimization',
    ],
  },
  {
    title: 'Inventory & Pricing Management',
    gradient: 'linear-gradient(135deg, #16a34a, #10b981)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="16.5" y1="9.4" x2="7.5" y2="4.21" />
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
        <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
        <line x1="12" y1="22.08" x2="12" y2="12" />
      </svg>
    ),
    points: [
      'Inventory forecasting',
      'Replenishment planning',
      'Pricing optimization',
      'Competitive pricing analysis',
      'Stock monitoring',
      'Aging inventory analysis',
      'Promotional pricing strategy',
    ],
  },
  {
    title: 'Analytics & Reporting',
    gradient: 'linear-gradient(135deg, #475569, #334155)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
      </svg>
    ),
    points: [
      'Sales reports',
      'Advertising reports',
      'Product performance tracking',
      'Profitability analysis',
      'Inventory reports',
      'Competitor benchmarking',
      'Growth dashboards',
    ],
  },
];

const DIFFERENTIATORS = [
  'Dedicated marketplace specialists',
  'Regional GCC marketplace expertise',
  'SEO + Advertising integration',
  'Continuous optimization',
  'Performance-focused approach',
  'Transparent reporting',
  'Long-term growth strategies',
];

const INDUSTRIES = [
  'Beauty & Personal Care',
  'Fashion & Apparel',
  'Electronics',
  'Home & Kitchen',
  'Consumer Goods',
  'FMCG',
  'Luxury Brands',
  'Health & Wellness',
  'Pet Products',
  'D2C Brands',
];

const STAGES = [
  'UAE Marketplace Growth',
  'Saudi Arabia Expansion',
  'Egypt Marketplace Launch',
  'Multi-country scaling',
  'Yellow Friday campaigns',
  'Ramadan promotions',
];

const ADDITIONAL = [
  'Seasonal campaign planning',
  'Discount strategies',
  'Coupon management',
  'Account compliance monitoring',
  'Seller score improvement',
  'Profitability analysis',
  'Competitor benchmarking',
  'Market expansion support',
];

const FAQS = [
  {
    q: 'Do you help with Noon seller registration?',
    a: 'Yes. We assist with seller onboarding, account setup, document guidance, and store activation.',
  },
  {
    q: 'Can you manage Noon advertising campaigns?',
    a: 'Yes. We create, optimize, and scale Noon Ads campaigns to improve visibility and sales.',
  },
  {
    q: 'Do you support Fulfilled by Noon (FBN)?',
    a: 'Yes. We provide inventory planning, shipment support, and fulfillment strategy consulting for FBN operations.',
  },
  {
    q: 'Can you help expand into UAE, Saudi Arabia, and Egypt?',
    a: 'Yes. We help brands launch and scale across all major Noon-supported markets.',
  },
];

export default function NoonPage() {
  const [openFaq, setOpenFaq] = useState(null);
  const toggle = (i) => setOpenFaq(openFaq === i ? null : i);

  return (
    <>
      <SEOHead {...SEO} faqs={FAQS} />
      <Header />
      <main className="sp-page">

        {/* BREADCRUMB */}
        <nav className="sp-breadcrumb">
          <div className="sp-breadcrumb-inner">
            <Link to="/">Home</Link>
            <span className="sp-bc-sep">/</span>
            <Link to="/#services">Services</Link>
            <span className="sp-bc-sep">/</span>
            <span className="sp-bc-current">Noon Marketplace</span>
          </div>
        </nav>

        {/* HERO — single column, no image */}
        <section className="sp-hero">
          <div className="sp-hero-inner">
            <div className="sp-hero-tag">Noon Marketplace</div>
            <h1 className="sp-hero-h1">Noon Marketplace Management Services</h1>
            <p className="sp-hero-sub">Complete Noon Seller Account Management & Growth Solutions</p>
            <p className="sp-hero-desc">Expand your brand across the Middle East with professional Noon Marketplace management services. From seller onboarding and catalog management to Noon Ads, inventory optimization, fulfillment support, and marketplace growth strategies, we help businesses scale profitably on Noon UAE, Saudi Arabia, and Egypt.</p>
            <div className="sp-hero-stats">
              <div className="sp-hero-stat"><span className="sp-hero-stat-val">3</span><span className="sp-hero-stat-lbl">Markets Covered</span></div>
              <div className="sp-hero-stat"><span className="sp-hero-stat-val">UAE/KSA/EGY</span><span className="sp-hero-stat-lbl">Regional Reach</span></div>
              <div className="sp-hero-stat"><span className="sp-hero-stat-val">Full</span><span className="sp-hero-stat-lbl">Account Management</span></div>
            </div>
            <div className="sp-hero-btns">
              <a href="/#contact" className="sp-btn-primary">Get Free Audit</a>
              <a href="#services" className="sp-btn-outline">Explore Services</a>
            </div>
          </div>
        </section>

        {/* PLATFORM OVERVIEW — with Image 1 */}
        <section className="sp-sec" id="overview">
          <div className="sp-sec-inner">
            <div className="sp-sec-img-grid">
              <div>
                <div className="sp-label">Platform Overview</div>
                <h2 className="sp-h2">Sell & Scale on Noon with a Performance-Driven Strategy</h2>
                <p className="sp-para">Noon is one of the leading e-commerce marketplaces in the Middle East, serving customers across UAE, Saudi Arabia, and Egypt. The platform offers sellers access to marketplace infrastructure, payment processing, logistics support, fulfillment services, promotional campaigns, and advertising solutions through Seller Lab.</p>
                <p className="sp-para">Selling on Noon gives your brand:</p>
                <ul className="sp-bullet-list">
                  {OVERVIEW_POINTS.map((pt, i) => <li key={i} className="sp-bullet-item"><span className="sp-dot" />{pt}</li>)}
                </ul>
              </div>
              <div className="sp-img-box">
                <img src={img1} alt="Noon marketplace management" />
              </div>
            </div>
          </div>
        </section>

        {/* HOW WE HELP */}
        <section className="sp-sec sp-sec--alt">
          <div className="sp-sec-inner">
            <div className="sp-label">How We Help Your Brand</div>
            <h2 className="sp-h2">End-to-End Noon Marketplace Management</h2>
            <p className="sp-para">Managing a Noon store requires more than uploading products. Success depends on catalog optimization, advertising management, inventory planning, pricing strategies, fulfillment performance, and account compliance. We act as your dedicated marketplace growth team.</p>
            <h3 className="sp-h3">Our Focus Areas:</h3>
            <div className="sp-approach-grid">
              {APPROACH.map((item, i) => <div key={i} className="sp-approach-tag">{item}</div>)}
            </div>
            <p className="sp-para">We focus on building sustainable growth through performance optimization, regional brand positioning, and conversion-focused strategies across all Noon markets.</p>
          </div>
        </section>

        {/* OUR PROCESS */}
        <section className="sp-sec" id="process">
          <div className="sp-sec-inner">
            <div className="sp-label">Our Process</div>
            <h2 className="sp-h2">How We Work</h2>
            <div className="sp-process-list">
              {PROCESS.map((step, i) => (
                <div key={i} className="sp-process-step">
                  <div className="sp-process-left">
                    <div className="sp-process-num">{step.num}</div>
                    {i < PROCESS.length - 1 && <div className="sp-process-line" />}
                  </div>
                  <div className="sp-process-body">
                    <h3 className="sp-process-title">{step.title}</h3>
                    <p className="sp-process-intro">{step.intro}</p>
                    <ul className="sp-process-subs">
                      {step.points.map((pt, j) => <li key={j} className="sp-process-sub"><span className="sp-dot-sm" />{pt}</li>)}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SERVICES */}
        <section className="sp-sec sp-sec--alt" id="services">
          <div className="sp-sec-inner">
            <div className="sp-label">Services We Provide</div>
            <h2 className="sp-h2">Everything Your Noon Business Needs</h2>
            <div className="sp-cards">
              {SERVICES.map((s, i) => (
                <div key={i} className="sp-card">
                  <div className="sp-card-accent" style={{ background: s.gradient }} />
                  <div className="sp-card-icon" style={{ background: s.gradient }}>{s.icon}</div>
                  <h3 className="sp-card-title">{s.title}</h3>
                  <ul className="sp-card-list">
                    {s.points.map((pt, j) => <li key={j} className="sp-card-item"><span className="sp-dot-sm" />{pt}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* WHY CHOOSE — with Image 2 */}
        <section className="sp-sec">
          <div className="sp-sec-inner">
            <div className="sp-label">Why Choose Our Noon Marketplace Services</div>
            <h2 className="sp-h2">Marketplace Experts Focused on Growth</h2>
            <p className="sp-para">We combine marketplace expertise, advertising management, operational support, and data-driven optimization to help brands succeed on Noon across the GCC region.</p>
            <div className="sp-why-grid">
              <div>
                <h3 className="sp-h3">Benefits of Working With Us</h3>
                <ul className="sp-diff-list">
                  {DIFFERENTIATORS.map((d, i) => <li key={i} className="sp-diff-item"><span className="sp-check" />{d}</li>)}
                </ul>
              </div>
              <div className="sp-img-box">
                <img src={img2} alt="Noon marketplace management approach" />
              </div>
            </div>
            <h3 className="sp-h3">Industries We Serve</h3>
            <div className="sp-industry-wrap">
              {INDUSTRIES.map((ind, i) => <span key={i} className="sp-industry-chip">{ind}</span>)}
            </div>
          </div>
        </section>

        {/* BUSINESS STAGE */}
        <section className="sp-sec sp-sec--alt">
          <div className="sp-sec-inner">
            <h2 className="sp-h2">Marketplace Growth Solutions for Every Stage</h2>
            <p className="sp-para">Whether you are looking to expand into:</p>
            <div className="sp-stage-grid">
              {STAGES.map((s, i) => <div key={i} className="sp-stage-item"><span className="sp-dot" />{s}</div>)}
            </div>
            <p className="sp-para">Our team helps create a structured marketplace growth strategy tailored to your regional business goals.</p>
          </div>
        </section>

        {/* ADDITIONAL SERVICES */}
        <section className="sp-sec">
          <div className="sp-sec-inner">
            <div className="sp-label">Additional Growth Services</div>
            <div className="sp-additional-grid">
              {ADDITIONAL.map((item, i) => <div key={i} className="sp-additional-item"><span className="sp-check" />{item}</div>)}
            </div>
          </div>
        </section>

        {/* FAQ — accordion */}
        <section className="sp-sec sp-sec--alt">
          <div className="sp-sec-inner">
            <div className="sp-label">Frequently Asked Questions</div>
            <h2 className="sp-h2">Got Questions? We Have Answers.</h2>
            <div className="sp-faq-list">
              {FAQS.map((faq, i) => (
                <div key={i} className={`sp-faq-card${openFaq === i ? ' sp-faq-card--open' : ''}`}>
                  <button className="sp-faq-header" onClick={() => toggle(i)} aria-expanded={openFaq === i}>
                    <span className="sp-faq-q">{faq.q}</span>
                    <span className="sp-faq-chevron">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
                    </span>
                  </button>
                  {openFaq === i && <div className="sp-faq-body"><div className="sp-faq-a">{faq.a}</div></div>}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="sp-cta">
          <div className="sp-cta-inner">
            <h2 className="sp-cta-h2">Ready to Grow on Noon?</h2>
            <p className="sp-cta-desc">Scale your business across the Middle East with complete Noon Marketplace management services focused on visibility, conversions, operational efficiency, and long-term marketplace growth.</p>
            <a href="/#contact" className="sp-btn-white">Get Free Audit</a>
          </div>
        </section>

      </main>
      <Footer />
    </>
  );
}
